import type {ReactElement} from 'react';
import type {QRCalendarEventInput} from '@Libraries/qr-code/qr-code.types';
import {QRPayloadField, QR_EVENT_DESCRIPTION_MAX_LENGTH} from '@Libraries/qr-code/qr-code.types';
import {QRField} from '@Components/qr-code-generator/components/qr-field/qr-field';
import {QRFieldRow} from '@Components/qr-code-generator/components/qr-field-row/qr-field-row';
import {QRSelect} from '@Components/qr-code-generator/components/qr-select/qr-select';
import {InputFieldType} from '@Components/input-field-v2/input-field.types';
import {QR_UTC_OFFSET_OPTIONS} from '@Components/qr-code-generator/qr-code-generator.types';
import {Text, TextSize} from '@Components/text';
import type {QRFormProps} from '@Components/qr-code-generator/qr-code-generator.types';

export function QRCalendarEventForm({input, issues, onPatch}: QRFormProps<QRCalendarEventInput>): ReactElement {
  return (
    <>
      <QRFieldRow>
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_title_label')}
          placeholder={window.i18next.t('pmwjs_qr_generator_event_title_placeholder')}
          value={input.title}
          field={QRPayloadField.TITLE}
          issues={issues}
          onValueChange={(title): void => {
            onPatch({title});
          }}
        />
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_location_label')}
          placeholder={window.i18next.t('pmwjs_qr_generator_event_location_placeholder')}
          value={input.location}
          onValueChange={(location): void => {
            onPatch({location});
          }}
        />
      </QRFieldRow>
      <QRFieldRow>
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_start_date_label')}
          inputType={InputFieldType.DATE}
          value={input.startDate}
          field={QRPayloadField.STARTS_AT}
          issues={issues}
          onValueChange={(startDate): void => {
            onPatch({startDate});
          }}
        />
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_end_date_label')}
          inputType={InputFieldType.DATE}
          value={input.endDate}
          field={QRPayloadField.ENDS_AT}
          issues={issues}
          onValueChange={(endDate): void => {
            onPatch({endDate});
          }}
        />
      </QRFieldRow>
      <QRFieldRow>
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_start_time_label')}
          inputType={InputFieldType.TIME}
          value={input.startTime}
          onValueChange={(startTime): void => {
            onPatch({startTime});
          }}
        />
        <QRField
          label={window.i18next.t('pmwjs_qr_generator_event_end_time_label')}
          inputType={InputFieldType.TIME}
          value={input.endTime}
          onValueChange={(endTime): void => {
            onPatch({endTime});
          }}
        />
      </QRFieldRow>
      <QRSelect
        label={window.i18next.t('pmwjs_qr_generator_event_time_zone_label')}
        value={String(input.utcOffsetMinutes)}
        options={QR_UTC_OFFSET_OPTIONS}
        onValueChange={(offsetMinutes): void => {
          onPatch({utcOffsetMinutes: Number(offsetMinutes)});
        }}
      />
      <QRField
        label={window.i18next.t('pmwjs_qr_generator_event_description_label')}
        placeholder={window.i18next.t('pmwjs_qr_generator_event_description_placeholder')}
        value={input.description}
        field={QRPayloadField.DESCRIPTION}
        issues={issues}
        isMultiline
        maxLength={QR_EVENT_DESCRIPTION_MAX_LENGTH}
        onValueChange={(description): void => {
          onPatch({description});
        }}
      />
      <Text val={window.i18next.t('pmwjs_qr_generator_event_help')} size={TextSize.SM} />
    </>
  );
}
